import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  LinkIcon,
  LinkSlashIcon,
} from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { Columns, TableActions } from '@/components/table/types';
import { Tooltip } from '@/components/Tooltip';
import { type ReportItem } from '@/hooks/useReporting';
import { type MyFile } from '@/types';

/**
 * SharedFile interface for PlexTrac report files
 * Extends MyFile with additional report-specific metadata
 */
interface SharedFile extends MyFile {
  // MyFile provides: key, name, updated, username, class
  // Additional report-specific fields can be added here if needed
}

interface UseReportingTableColumnsProps {
  sharedFiles: MyFile[] | undefined;
  plexTracClientId: string | undefined;
  connectedReports: Set<string>;
  connectingReportId: string | null;
  onExportReport: (reportId: string) => Promise<void>;
  onDownloadReport: (report: ReportItem) => Promise<void>;
  onConnectReport: (report: ReportItem, isConnected: boolean) => Promise<void>;
  checkIfReportIsConnected: (reportId: string | number) => boolean;
}

/**
 * Hook for PlexTrac reporting table column definitions
 *
 * Provides table columns with export status, connection status, and action buttons.
 * Memoized to prevent unnecessary re-renders of table cells.
 *
 * @param sharedFiles - List of exported report files from S3
 * @param plexTracClientId - Client ID for matching exported files
 * @param connectedReports - Set of connected report IDs
 * @param connectingReportId - Currently connecting report ID (for loading state)
 * @param onExportReport - Handler for exporting report to PDF
 * @param onDownloadReport - Handler for downloading exported PDF
 * @param onConnectReport - Handler for connecting/disconnecting report
 * @param checkIfReportIsConnected - Function to check if report is connected
 * @returns Array of table column definitions and row actions
 */
export function useReportingTableColumns({
  sharedFiles,
  plexTracClientId,
  connectedReports,
  connectingReportId,
  onExportReport,
  onDownloadReport,
  onConnectReport,
  checkIfReportIsConnected,
}: UseReportingTableColumnsProps): {
  columns: Columns<ReportItem>;
  rowActions: (report: ReportItem) => TableActions;
} {
  /**
   * Find exported file for a specific PlexTrac report
   *
   * Matches files by pattern: reports/plextrac-report-{clientId}-{reportId}-
   *
   * @param reportId - PlexTrac report ID
   * @returns SharedFile if found, null otherwise
   */
  const getExportedFileForReport = useMemo(() => {
    return (reportId: string | number): SharedFile | null => {
      if (!sharedFiles || !plexTracClientId) return null;

      const reportIdStr = String(reportId);
      const pattern = `reports/plextrac-report-${plexTracClientId}-${reportIdStr}-`;

      return (
        (sharedFiles as SharedFile[]).find((file: SharedFile) => {
          return file.name && file.name.startsWith(pattern);
        }) || null
      );
    };
  }, [sharedFiles, plexTracClientId]);

  /**
   * Format timestamp to readable date string
   *
   * @param timestamp - Unix timestamp in milliseconds
   * @returns Formatted date string or null if invalid
   */
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return null;

    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const columns: Columns<ReportItem> = useMemo(() => {
    return [
      {
        label: 'Report Title',
        id: 'name',
        cell: report => (
          <span className="text-chariot-text-primary truncate text-xs">
            <Tooltip title={report.name}>{report.name}</Tooltip>
          </span>
        ),
      },
      {
        label: 'Status',
        id: 'status',
        cell: report => {
          if (report.status === 'Published') {
            return (
              <div className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-2.5 py-1 text-xs font-medium text-green-500">
                <CheckCircleIcon className="size-3" />
                Published
              </div>
            );
          }
          if (report.status === 'Draft') {
            return (
              <div className="inline-flex items-center gap-1 rounded-full bg-gray-500/20 px-2.5 py-1 text-xs font-medium text-gray-400">
                Draft
              </div>
            );
          }
          return null;
        },
        fixedWidth: 120,
      },
      {
        label: 'Date Created',
        id: 'created_at',
        cell: report => (
          <span className="text-chariot-text-secondary text-xs">
            {report.created_at ? formatDate(report.created_at) : '-'}
          </span>
        ),
        fixedWidth: 130,
      },
      {
        label: 'Report ID',
        id: 'id',
        cell: report => (
          <span className="text-chariot-text-secondary/70 font-mono text-xs">
            {report.id}
          </span>
        ),
        fixedWidth: 100,
      },
      {
        label: 'Export Status',
        id: '',
        cell: report => {
          const exportedFile = getExportedFileForReport(report.id);

          if (exportedFile) {
            return (
              <div className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-2.5 py-1 text-xs font-medium text-green-500">
                <CheckCircleIcon className="size-3" />
                Exported
              </div>
            );
          }

          return (
            <div className="inline-flex items-center gap-1 rounded-full bg-gray-500/20 px-2.5 py-1 text-xs font-medium text-gray-400">
              Not Exported
            </div>
          );
        },
        fixedWidth: 130,
      },
      {
        label: 'Connection Status',
        id: '',
        cell: report => {
          const isConnected = checkIfReportIsConnected(report.id);

          if (isConnected) {
            return (
              <div className="inline-flex items-center gap-1 rounded-full bg-green-500/20 px-2.5 py-1 text-xs font-medium text-green-500">
                <CheckCircleIcon className="size-3" />
                Connected
              </div>
            );
          }

          return (
            <div className="inline-flex items-center gap-1 rounded-full bg-gray-500/20 px-2.5 py-1 text-xs font-medium text-gray-400">
              Not Connected
            </div>
          );
        },
        fixedWidth: 130,
      },
      {
        label: 'Actions',
        id: '',
        cell: report => {
          const exportedFile = getExportedFileForReport(report.id);
          const isConnected = checkIfReportIsConnected(report.id);
          const isConnecting = connectingReportId === report.id;

          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onConnectReport(report, isConnected)}
                disabled={isConnecting}
                className="hover:bg-chariot-background-tertiary flex size-8 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                title={`${isConnected ? 'Disconnect report from Guard' : 'Connect report to Guard'}`}
              >
                {isConnecting ? (
                  <Loader isLoading type="spinner" />
                ) : isConnected ? (
                  <LinkSlashIcon className="text-chariot-text-primary size-5" />
                ) : (
                  <LinkIcon className="text-chariot-text-primary size-5" />
                )}
              </button>

              {report.status === 'Published' ? (
                <button
                  onClick={() => onExportReport(report.id)}
                  className="hover:bg-chariot-background-tertiary flex size-8 items-center justify-center rounded-lg transition-colors"
                  title="Export report from PlexTrac"
                >
                  <DocumentArrowDownIcon className="text-chariot-text-primary size-5" />
                </button>
              ) : (
                <button
                  className="hover:bg-chariot-background-tertiary flex size-8 items-center justify-center rounded-lg opacity-50 transition-colors"
                  title="Not Published: Publish report in PlexTrac first to export"
                  disabled
                >
                  <DocumentArrowDownIcon className="text-chariot-text-primary size-5" />
                </button>
              )}

              {exportedFile && report.status === 'Published' ? (
                <button
                  onClick={() => onDownloadReport(report)}
                  className="hover:bg-chariot-background-tertiary flex size-8 items-center justify-center rounded-lg transition-colors"
                  title="Download exported report"
                >
                  <ArrowDownTrayIcon className="text-chariot-text-primary size-5" />
                </button>
              ) : (
                <button
                  className="hover:bg-chariot-background-tertiary flex size-8 items-center justify-center rounded-lg opacity-50 transition-colors"
                  title="Not Exported: Export report from PlexTrac first to download"
                  disabled
                >
                  <ArrowDownTrayIcon className="text-chariot-text-primary size-5" />
                </button>
              )}
            </div>
          );
        },
        fixedWidth: 140,
        align: 'center',
      },
    ];
  }, [
    getExportedFileForReport,
    checkIfReportIsConnected,
    connectingReportId,
    onExportReport,
    onDownloadReport,
    onConnectReport,
  ]);

  const rowActions = (report: ReportItem): TableActions => {
    const exportedFile = getExportedFileForReport(report.id);
    const isConnected = checkIfReportIsConnected(report.id);

    const actions = [];

    actions.push({
      label: isConnected ? 'Disconnect' : 'Connect',
      onClick: () => onConnectReport(report, isConnected),
    });

    actions.push({
      label: 'Export',
      onClick: () => onExportReport(report.id),
    });

    if (exportedFile) {
      actions.push({
        label: 'Download',
        onClick: () => onDownloadReport(report),
      });
    }

    return {
      menu: {
        items: actions.map(action => ({
          label: action.label,
          value: action.label,
          onClick: action.onClick,
        })),
      },
    };
  };

  return { columns, rowActions };
}
